import { useState, useMemo, useEffect } from 'react';
import { MdDeliveryDining, MdRestaurant, MdHistory, MdCalendarToday } from 'react-icons/md';
import { AiOutlineLoading, AiOutlineInbox } from "react-icons/ai";
import { useAuth } from '../../auth/AuthenticationWrapper';
import { useOrdersLogic } from './hooks/useOrdersLogic';
import { getBrazilDateString } from '../../utils/DateUtils';
import { PrepReport } from '../../components/prepReport';
import ModalPortal from '../../components/lib/modalPortal';
import { CancelOrderModal } from './components/cancelOrderModal';
import { ConfirmAlternativePaymentModal } from './components/confirmAlternativePaymentModal';
import { OrderCard } from '../../components/orderCard';
import SearchInput from '../../components/reusables/searchInput';

const TabButton = ({ active, onClick, icon: Icon, label }) => (
    <button onClick={onClick} className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold uppercase tracking-wider transition-all border-b-4 ${active ? "border-custom_blue text-custom_blue bg-blue-50" : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}>
        <Icon size={20} /> <span className="hidden sm:inline">{label}</span>
    </button>
);

function OrdersPage() {
    const { data, filters, actions, helpers } = useOrdersLogic();
    const { filteredOrders, groupedOrders, ordersStatus, loading, preferences } = data;

    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 12;

    useEffect(() => setCurrentPage(0), [filteredOrders, ordersStatus]);

    const paginatedOrders = useMemo(() => {
        const start = currentPage * pageSize;
        return filteredOrders.slice(start, start + pageSize);
    }, [filteredOrders, currentPage]);

    const maxPage = Math.ceil(filteredOrders.length / pageSize);

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [modalType, setModalType] = useState(null);

    const { hasPermission } = useAuth();
    const permissions = {
        canChange: hasPermission("orders", "change_status"),
        canCancel: hasPermission("orders", "cancel_order")
    };

    const handleAction = (type, order) => {
        setSelectedOrder(order);
        if (type === 'cancel') setModalType('cancel');
        if (type === 'confirmPayment') setModalType('confirmPayment');
        if (type === 'finish') {
            if (preferences?.enable_confirm_ordercompletion) setModalType('finish');
            else actions.finishOrder(order);
        }
        if (type === 'deliver') actions.deliverOrder(order);
    };

    const handleDateChange = (e) => {
        const selectedDate = e.target.value; 
        if (!selectedDate) return;
        const newRange = {
            start: `${selectedDate}T00:00:00`,
            end: `${selectedDate}T23:59:59`
        };
        actions.handleDateRangeChange(newRange);
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 font-poppins">
            <PrepReport data={data} filters={filters} />

            <div className="bg-white shadow-sm sticky top-0 z-20">
                <div className="p-3 border-b border-slate-100 flex flex-col sm:flex-row w-full gap-4 items-center">
                    <div className="flex-1 w-full">
                        <SearchInput
                            value={filters.search}
                            onChange={actions.handleSearchChange}
                            placeholder="Pesquisar por nome do aluno ou ID do pedido..."
                        />
                    </div>

                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 rounded-lg transition-all hover:border-custom_blue">
                        <MdCalendarToday className="text-custom_blue" size={18} />
                        <input
                            type="date"
                            className="bg-transparent text-sm font-bold text-gray-600 outline-none cursor-pointer"
                            onChange={handleDateChange}
                            defaultValue={getBrazilDateString()}
                        />
                    </div>
                </div>

                <div className="flex w-full border-b border-slate-100">
                    <TabButton active={ordersStatus === 'inProgress'} onClick={() => actions.handleStatusChange('inProgress')} icon={MdRestaurant} label="Em Preparo" />
                    <TabButton active={ordersStatus === 'toDeliver'} onClick={() => actions.handleStatusChange('toDeliver')} icon={MdDeliveryDining} label="Prontos / Entrega" />
                    <TabButton active={ordersStatus === 'done'} onClick={() => actions.handleStatusChange('done')} icon={MdHistory} label="Histórico" />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-slate-100">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <AiOutlineLoading className="animate-spin text-4xl mb-2 text-custom_blue" />
                        <p>Carregando...</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        <AiOutlineInbox size={48} className="mb-2 opacity-20" />
                        <p>Nenhum pedido para esta data.</p>
                    </div>
                ) : ordersStatus === 'done' ? (
                    Object.keys(groupedOrders).map(date => (
                        <div key={date} className="mb-8">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="bg-white border border-slate-200 text-slate-600 px-4 py-1 rounded-full text-sm font-bold shadow-sm">
                                    {date}
                                </span>
                                <div className="h-px bg-slate-200 flex-1"></div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {groupedOrders[date].map(order => (
                                    <OrderCard
                                        key={order.order_id}
                                        order={order}
                                        currentTab={ordersStatus}
                                        hasPrep={helpers.orderHasPrepItems(order)}
                                        onAction={handleAction}
                                        permissions={permissions}
                                        loading={loading}
                                    />
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-20">
                        {paginatedOrders.map(order => (
                            <OrderCard
                                key={order.order_id}
                                order={order}
                                currentTab={ordersStatus}
                                hasPrep={helpers.orderHasPrepItems(order)}
                                onAction={handleAction}
                                permissions={permissions}
                                loading={loading}
                            />
                        ))}
                    </div>
                )}
            </div>

            {ordersStatus !== 'done' && maxPage > 1 && (
                <div className="bg-white p-3 border-t border-slate-200 flex justify-between items-center shadow-lg z-20">
                    <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 text-sm font-bold text-gray-600">Anterior</button>
                    <span className="text-sm font-medium text-gray-600">Página {currentPage + 1} de {maxPage}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(maxPage - 1, p + 1))} disabled={currentPage === maxPage - 1} className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 text-sm font-bold text-gray-600">Próxima</button>
                </div>
            )}

            <CancelOrderModal
                isOpen={modalType === 'cancel'}
                onClose={() => setModalType(null)}
                order={selectedOrder}
                loading={loading}
                onConfirm={(o, m) => { actions.cancelOrder(o, m); setModalType(null); }}
            />

            <ConfirmAlternativePaymentModal
                isOpen={modalType === 'confirmPayment'}
                order={selectedOrder}
                onClose={() => setModalType(null)}
                onSuccess={(orderId) => {
                    actions.updateOrderInList(orderId, {
                        status: 'COMPLETED',
                        last_update: new Date().toISOString(),
                    });
                    setModalType(null);
                }}
            />

            {modalType === 'finish' && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm text-center">
                            <h3 className="text-xl font-bold mb-2">Finalizar Pedido?</h3>
                            <p className="text-gray-500 mb-6 text-sm">O pedido será marcado como entregue.</p>
                            <div className="flex gap-3">
                                <button onClick={() => setModalType(null)} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">Voltar</button>
                                <button onClick={() => { actions.finishOrder(selectedOrder); setModalType(null); }} className="flex-1 py-3 bg-green-500 text-white rounded-lg font-bold">Confirmar</button>
                            </div>
                        </div>
                    </div>
                </ModalPortal>
            )}
        </div>
    );
}

export default OrdersPage;